import type { NextPage, NextPageContext } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import React from 'react';
import { DocumentBody } from 'src/components/document/body.component';
import { PageLayout } from 'src/components/layout/pageLayout.component';
import type { Document } from 'src/types/documentation';
import { formatDay } from 'src/utils/format';
import { fetchDoc } from 'src/utils/sanity.server';
import { searchForAllCountriesStaticProps } from '../actions/searchActions';

interface AboutProps {
  document: Pick<Document, 'title' | 'content' | 'date'>;
}

const About: NextPage<AboutProps> = ({ document }) => {
  const { t } = useTranslation('about');

  return (
    <PageLayout
      title={t('about:seo.title')}
      description={t('about:seo.description')}
      subtitle={
        document.date ? `Last updated ${formatDay(document.date)}` : undefined
      }
      hasTitleBanner
    >
      <DocumentBody
        html={
          '<p><strong>The Population Project</strong> is a non-profit organization, incorporated in 2021 the state of New York, USA. It has been granted tax-exempt 501(c)(3) status on July 23, 2021. Its Employer Identification Number is 86-3242533.</p>\n' +
          '<p>Board members are:</p>\n' +
          '<ul>\n' +
          '<li><a href="https://www.linkedin.com/in/antoine-bello-7282845/" target="_blank" rel="nofollow">Antoine Bello</a>, 52, co-founder of\n' +
          '<a href="https://ubiqus.com/" target="_blank">Ubiqus</a>, founder of The Sports Oracle and\n' +
          'novelist at <a href="http://www.gallimard.fr/Contributeurs/Antoine-Bello" target="_blank">Gallimard</a>.</li>\n' +
          '<li><a href="https://www.linkedin.com/in/vincentbello/" target="_blank" rel="nofollow">Vincent Bello</a>, 28, senior software engineer at\n' +
          'Netflix and founder of <a href="https://apps.apple.com/us/app/my-reel/id1511444278" target="_blank" rel="nofollow">Reel</a>.</li>\n' +
          '<li><a href="https://www.linkedin.com/in/berangere-callens-5a54258/" target="_blank" rel="nofollow">Bérangère Callens</a>, 50, interior\n' +
          'architect and literary translator.</li>\n' +
          '</ul>\n'
        }
      />
    </PageLayout>
  );
};

export default About;

export const getStaticProps = async ({ locale }: NextPageContext) => {
  let document, revalidate, translations;
  const countries = await searchForAllCountriesStaticProps();

  try {
    [{ doc: document, revalidate }, translations] = await Promise.all([
      fetchDoc({
        docType: 'about',
        slug: 'about',
      }),
      serverSideTranslations(locale ?? 'en', [
        'common',
        'about',
        'navbar',
        'new-homepage',
        'new-navbar',
        'new-footer',
      ]),
    ]);
  } catch (error) {
    console.error(error);
  }

  return {
    props: {
      ...(translations ?? {}),
      document,
      countries: countries || [],
    },
    revalidate,
  };
};
